import { inject, Injectable } from '@angular/core';
import { CampusService } from '@app/pages/campus/services/campus.service';
import { IntakeService } from '@app/pages/intake/services/intake.service';
import { ApiService } from '@app/shared/services/api.service';
import { Observable, take } from 'rxjs';
import { DepartmentService } from '@app/pages/department/services/department.service';
import { ErService } from '@app/pages/entry-requirement/services/er.service';
import { ERQualType } from '@app/shared/enm/entry-requirement.enum';
import { InstitutionService } from '@app/pages/institution/service/institution.service';
import { CourseService } from '@app/pages/course/services/course.service';
import { HomeService } from '@app/pages/home/services/home.service';
import { CourseType } from '@app/shared/enm/course-instance.enum';
import { AvailabilityService } from '@app/pages/availability/services/availability.service';
import { AccessControlService } from './access-control.service';
import { ConstantVariables } from '@app/shared/util/constant-variables';
import { NonEduOrgService } from '@app/pages/non-edu-org/service/non-edu-org.service';

@Injectable({
  providedIn: 'root'
})
export class DataLoaderService {
  private erService = inject(ErService);
  private intakeService = inject(IntakeService);
  private campusService = inject(CampusService);
  private courseService = inject(CourseService);
  private homeApiService = inject(HomeService);
  private sharedApiService = inject(ApiService);
  private departmentService = inject(DepartmentService);
  private institutionService = inject(InstitutionService);
  private availabilityService = inject(AvailabilityService);
  private accessControlService = inject(AccessControlService);
  private nonEduOrgService = inject(NonEduOrgService);

  private constVariable = inject(ConstantVariables);

  private countryId: any;
  private institutionId: any;

  loadEntityData(url: string): { [key: string]: Observable<any> } | null {
    // remove query params before operating
    url = url.split('?')?.[0];
    if (!url.includes('institution') && !url.includes('home') && !url.includes('non-edu-org')) {
      this.institutionId = JSON.parse(localStorage.getItem("institutionDetails") ?? '')?.institutionId;
      this.countryId = JSON.parse(localStorage.getItem("countryDetails") ?? '')?.countryId;
    }
    if (url.includes('manage')) return this.listingPageHandler(url);
    else if (url.includes('home')) return this.homeInitDataLoad();
    else if (url.includes('intake')) return this.intakeInitDataLoad(url);
    else if (url.includes('campus')) return this.campusInitDataLoad(url);
    else if (url.includes('department')) return this.departmentInitDataLoad(url);
    else if (url.includes('er')) return this.detailedERInitDataLoad(url);
    else if (url.includes('institution')) return this.institutionInitDataLoad(url);
    else if (url.includes('course-availability')) return this.availabilityInitDataLoad(url);
    else if (url.includes('course')) return this.courseInitDataLoad(url);
    else if (url.includes('non-edu-org')) return this.nonEduOrgInitDataLoad(url);
    return null;
  }

  private homeInitDataLoad(): { [key: string]: Observable<any> } {
    const apiCallList: { [key: string]: Observable<any> } = {};
    apiCallList['countryData'] = this.sharedApiService.getCountryData()?.pipe(take(1));
    return apiCallList;
  }

  private intakeInitDataLoad(url: string): { [key: string]: Observable<any> } {
    const apiCallList: { [key: string]: Observable<any> } = {};

    apiCallList['json'] = this.intakeService.getIntakeJSONDetails()?.pipe(take(1));
    apiCallList['ref'] = this.intakeService.fetchUnitDescDetails()?.pipe(take(1));

    if (['edit', 'view'].some(page => url.includes(page))) {
      const intakeId = url.split('/')[2];
      apiCallList['details'] = this.intakeService.getViewIntakeDetails(this.institutionId, intakeId, 'resolver')?.pipe(take(1));
    }
    return apiCallList;
  }

  private campusInitDataLoad(url: string): { [key: string]: Observable<any> } {
    const apiCallList: { [key: string]: Observable<any> } = {};

    apiCallList['countryData'] = this.sharedApiService.getCountryData().pipe(take(1));
    apiCallList['json'] = this.campusService.getJSON().pipe(take(1));
    apiCallList['ref'] = this.campusService.fetchCourseTypeDetails().pipe(take(1));

    if (['edit', 'view'].some(page => url.includes(page))) {
      const campusId = url.split('/')[2];
      apiCallList['details'] = this.campusService.getViewCampusData(this.institutionId, campusId, 'resolver').pipe(take(1));
    }
    return apiCallList;
  }

  private departmentInitDataLoad(url: string): { [key: string]: Observable<any> } {
    const apiCallList: { [key: string]: Observable<any> } = {};

    apiCallList['countryData'] = this.sharedApiService.getCountryData().pipe(take(1));
    apiCallList['json'] = this.departmentService.getDepartmentJSON().pipe(take(1));
    apiCallList['ref'] = this.departmentService.getDeptmentType().pipe(take(1));
    apiCallList['departmentListPage'] = this.departmentService.getDepartmentListingPageDetails(this.institutionId, 1, 'L-D', null, null, null, 'resolver').pipe(take(1));

    if (['edit', 'view'].some(page => url.includes(page))) {
      const departmentId = url.split('/')[2];
      apiCallList['details'] = this.departmentService.getDepartmentDetails(this.institutionId, departmentId, 'resolver').pipe(take(1));
    }
    return apiCallList;
  }

  private detailedERInitDataLoad(url: string): { [key: string]: Observable<any> } {
    const apiCallList: { [key: string]: Observable<any> } = {};
    const erTypeId = url.split('/')?.[2];
    const erEntityId = url.split('/')?.[3];
    const erType = (erTypeId === ERQualType.Tests) ? 'tests' : 'qualifications';

    apiCallList['ref'] = this.erService.getERRefData().pipe(take(1));
    apiCallList['json'] = this.erService.getERJson(erTypeId).pipe(take(1));

    if (['edit', 'view'].some(page => url.includes(page))) apiCallList['details'] = this.erService.getERDetails(erType, erEntityId, 'resolver').pipe(take(1));
    if (erType === 'qualifications') apiCallList['countryData'] = this.sharedApiService.getCountryData().pipe(take(1));
    return apiCallList;
  }

  private institutionInitDataLoad(url: string): { [key: string]: Observable<any> } {
    const apiCallList: { [key: string]: Observable<any> } = {};

    apiCallList['json'] = this.institutionService.getInstitutionJson()?.pipe(take(1));
    apiCallList['ref'] = this.institutionService.getInstitutionRefData()?.pipe(take(1));

    if (['edit', 'view'].some(page => url.includes(page))) {
      const institutionId = url.split('/')[2];
      apiCallList['details'] = this.institutionService.getInstitutionDetails(institutionId, 'resolver')?.pipe(take(1));
    }
    return apiCallList;
  }

  private courseInitDataLoad(url: string): { [key: string]: Observable<any> } {
    const apiCallList: { [key: string]: Observable<any> } = {};
    const courseTypeId = url.split('/')?.[2];
    const courseId = url.split('/')?.[3];
    const courseInstanceId = url.split('/')?.[4];

    // Course calls
    apiCallList['courseJson'] = this.courseService.getCourseJSON(courseTypeId)?.pipe(take(1));
    apiCallList['courseRef'] = this.courseService.getCourseReferenceData()?.pipe(take(1));

    if (courseTypeId === CourseType.GeneralCourseTypeId) apiCallList['institutionDetails'] = this.homeApiService.getInstitutionDetails(null, { 'resolver': 'true' }).pipe(take(1));
    if (['edit', 'view'].some(page => url.includes(page))) apiCallList['courseDetails'] = this.courseService.getViewCourseDetails(this.institutionId, courseId, 'resolver')?.pipe(take(1));

    // Instance calls
    if (courseInstanceId || url.includes('add-course')) {
      apiCallList['courseInstanceJson'] = this.courseService.getCourseInstanceJSON(courseTypeId)?.pipe(take(1));
      apiCallList['courseInstanceRef'] = this.courseService.getInstanceReferenceData()?.pipe(take(1));
      apiCallList['legacySubject'] = this.courseService.getInstanceReferenceData({ filterBy: 'IDPLegacySubject', countryId: JSON.parse(localStorage.getItem("countryDetails") ?? '')?.countryId }, 'resolver')?.pipe(take(1));

      if (courseTypeId === CourseType.GeneralCourseTypeId) apiCallList['departmentListPage'] = this.departmentService.getDepartmentListingPageDetails(this.institutionId, 1, 'L-D', null, null, null, 'resolver').pipe(take(1));

      if (courseInstanceId === 'n' || url.includes('add-course'))
        apiCallList['institutionDetails'] = this.institutionService.getInstitutionDetails(this.institutionId, 'resolver')?.pipe(take(1));

      if (['edit', 'view'].some(page => url.includes(page)) && courseInstanceId !== 'n') {
        if (courseTypeId === CourseType.GeneralCourseTypeId) {
          apiCallList['pathwayCourseFieldAccess'] = this.accessControlService.getFieldAccess('pathway-course', true).pipe(take(1));
          apiCallList['pathwayCourseActionAccess'] = this.accessControlService.getUserActionAccess('pathway-course', true).pipe(take(1));

          if (this.constVariable.ukCountryId === this.countryId) {
            apiCallList['stagesModulesFieldAccess'] = this.accessControlService.getFieldAccess('stages-modules', true).pipe(take(1));
            apiCallList['stagesModulesActionAccess'] = this.accessControlService.getUserActionAccess('stages-modules', true).pipe(take(1));
          }

          if (this.constVariable.australiaCountryId === this.countryId) {
            apiCallList['courseGroupTypeFieldAccess'] = this.accessControlService.getFieldAccess('course-group-type', true).pipe(take(1));
            apiCallList['courseGroupTypeActionAccess'] = this.accessControlService.getUserActionAccess('course-group-type', true).pipe(take(1));
          }
        }
        apiCallList['courseInstanceDetails'] = this.courseService.getViewCourseInstanceDetails(this.institutionId, courseId, courseInstanceId, 'resolver')?.pipe(take(1));
      }
    }

    return apiCallList;
  }

  private availabilityInitDataLoad(url: string): { [key: string]: Observable<any> } {
    const apiCallList: { [key: string]: Observable<any> } = {};
    const courseId = url.split('/')?.[3];
    const courseInstanceId = url.split('/')?.[4];

    apiCallList['feeFieldAccess'] = this.accessControlService.getFieldAccess('fee', true).pipe(take(1));
    apiCallList['feeActionAccess'] = this.accessControlService.getUserActionAccess('fee', true).pipe(take(1));
    apiCallList['availabilityJson'] = this.availabilityService.getAvailabilityJSONDetails()?.pipe(take(1));
    apiCallList['availabilityRef'] = this.availabilityService.getAvalReferenceData()?.pipe(take(1));
    apiCallList['courseInstanceRef'] = this.courseService.getInstanceReferenceData()?.pipe(take(1));
    apiCallList['campusListPage'] = this.campusService.getCampusDetails(this.institutionId, 1, null, null, { status: 'LIVE' }, 'availability', '', 1000, 'resolver')?.pipe(take(1));
    if (courseId && courseInstanceId)
      apiCallList['courseInstanceDetails'] = this.courseService.getViewCourseInstanceDetails(this.institutionId, courseId, courseInstanceId, 'resolver')?.pipe(take(1));
    else if(!courseId)
      apiCallList['courseListPage'] = this.courseService.getCoursesData(1, '', '', { status: 'LIVE' }, '', 5000, 'availability', 'resolver')?.pipe(take(1));
    return apiCallList;
  }

  private listingPageHandler(url: string): { [key: string]: Observable<any> } | null {
    const apiCallList: { [key: string]: Observable<any> } = {};

    if ((url as string).endsWith('manage-course')) {
      apiCallList['courseInstanceRef'] = this.courseService.getInstanceReferenceData()?.pipe(take(1));
      apiCallList['courseListPage'] = this.courseService.getCoursesData(1, '', '', { 'resolver': 'true' })?.pipe(take(1));

      return apiCallList;
    }
    else if (url.includes('/manage-course/instance')) {
      apiCallList['pathwayCourseFieldAccess'] = this.accessControlService.getFieldAccess('pathway-course', true).pipe(take(1));
      apiCallList['pathwayCourseActionAccess'] = this.accessControlService.getUserActionAccess('pathway-course', true).pipe(take(1));

      return apiCallList;
    }
    else if (url.includes('/manage-clearing')) {
      apiCallList['clearingInstanceListPage'] = this.courseService.getCourseInstanceData(1, 0, '', '', { 'resolver': 'true' }, false, null, true)?.pipe(take(1));
      apiCallList['pathwayCourseFieldAccess'] = this.accessControlService.getFieldAccess('pathway-course', true).pipe(take(1));
      apiCallList['pathwayCourseActionAccess'] = this.accessControlService.getUserActionAccess('pathway-course', true).pipe(take(1));

      return apiCallList;
    }
    return null;
  }

  private nonEduOrgInitDataLoad(url: string): { [key: string]: Observable<any> } | null {
    const apiCallList: { [key: string]: Observable<any> } = {};

    if (['add', 'view', 'edit'].some(page => url.includes(page)))
      apiCallList['json'] = this.nonEduOrgService.getNonEduOrgJson()?.pipe(take(1));

    if (['edit', 'view'].some(page => url.includes(page))) {
      const nonEduOrgId = url.split('/')[2];
      apiCallList['details'] = this.nonEduOrgService.getNonEduOrgDetailById(nonEduOrgId, 'resolver')?.pipe(take(1));
    }

    if (['add', 'edit', 'manage'].some(page => url.includes(page))) {
      apiCallList['countryData'] = this.sharedApiService.getCountryData()?.pipe(take(1));
      apiCallList['ref'] = this.nonEduOrgService.fetchReferenceData()?.pipe(take(1));
    }
    return apiCallList;
  }
}